import React, { useEffect, useMemo, useState } from "react";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../lib/authFetch";

const RANGE_KEYS = ["caba", "gba1", "gba2", "gba3"] as const;
type RangeKey = (typeof RANGE_KEYS)[number];
type PriceListType = "store" | "driver";
type CoverageMode = "cp" | "zone";

type CoverageItem = {
  mode: CoverageMode;
  postalCodes: string;
  zone: string;
};

type Coverage = Record<RangeKey, CoverageItem>;

type FormState = {
  id: number | null;
  code: string;
  name: string;
  type: PriceListType;
  priceCABA: string | number;
  priceGBA1: string | number;
  priceGBA2: string | number;
  priceGBA3: string | number;
  // Turbo es una MODALIDAD de servicio, no una zona postal — vive aparte de
  // coverage/RANGE_KEYS, nunca se le asigna CP. Vacío = TURBO_PRICE_NOT_CONFIGURED
  // (no se infiere de CABA/GBA*), no se fuerza a cargar en todas las tarifas.
  priceTurbo: string | number;
  coverage: Coverage;
};

type TariffApiRow = {
  id?: number;
  name?: string;
  type?: string;
  price_caba?: number | string;
  price_gba1?: number | string;
  price_gba2?: number | string;
  price_gba3?: number | string;
  price_turbo?: number | string | null;
  postal_codes?: Partial<Record<"CABA" | "GBA1" | "GBA2" | "GBA3", string[]>>;
};

type PriceListItem = {
  id: number | null;
  code: string;
  name: string;
  rawName: string;
  type: PriceListType;
  priceCABA: number | string;
  priceGBA1: number | string;
  priceGBA2: number | string;
  priceGBA3: number | string;
  priceTurbo: number | string;
  coverage: Coverage;
};

type PostalTier = "CABA" | "GBA1" | "GBA2" | "GBA3";

type PostalCodesPayload = Partial<Record<PostalTier, string[]>>;

type TariffsResponse = {
  ok?: boolean;
  error?: string;
  message?: string;
  tariffs?: TariffApiRow[];
  tariff?: TariffApiRow;
  tariffID?: number;
  id?: number;
  data?: { id?: number } | TariffApiRow[] | TariffApiRow;
};

type ModalProps = {
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
};

const emptyCoverage = (): Coverage => ({
  caba: { mode: "cp", postalCodes: "", zone: "" },
  gba1: { mode: "cp", postalCodes: "", zone: "" },
  gba2: { mode: "cp", postalCodes: "", zone: "" },
  gba3: { mode: "cp", postalCodes: "", zone: "" },
});

const emptyForm: FormState = {
  id: null,
  code: "",
  name: "",
  type: "store",
  priceCABA: "",
  priceGBA1: "",
  priceGBA2: "",
  priceGBA3: "",
  priceTurbo: "",
  coverage: emptyCoverage(),
};

const formatMoney = (value: number | string) => {
  const amount =
    typeof value === "number" ? value : Number(value || 0);

  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    maximumFractionDigits: 0,
  }).format(amount);
};

function apiTypeToUiType(value?: string): PriceListType {
  const normalized = String(value || "").trim().toUpperCase();
  return normalized === "DRIVER" ? "driver" : "store";
}

function uiTypeToApiType(value: PriceListType): "DRIVER" | "STORE" {
  return value === "driver" ? "DRIVER" : "STORE";
}

function sanitizePostalCodes(value = "") {
  let cleaned = value.replace(/[^\d,]/g, "");
  cleaned = cleaned.replace(/,+/g, ",");
  cleaned = cleaned.replace(/^,/, "");
  return cleaned;
}

function isValidPostalCodes(value = "") {
  if (!value.trim()) return true;
  return /^\d{4}(,\d{4})*$/.test(value.trim());
}

function tierToRangeKey(tier: PostalTier): RangeKey {
  switch (tier) {
    case "CABA":
      return "caba";
    case "GBA1":
      return "gba1";
    case "GBA2":
      return "gba2";
    case "GBA3":
      return "gba3";
    default:
      return "caba";
  }
}

function rangeKeyToTier(rangeKey: RangeKey): PostalTier {
  switch (rangeKey) {
    case "caba":
      return "CABA";
    case "gba1":
      return "GBA1";
    case "gba2":
      return "GBA2";
    case "gba3":
      return "GBA3";
    default:
      return "CABA";
  }
}

function parsePostalCodes(value = ""): string[] {
  return Array.from(
    new Set(
      value
        .split(",")
        .map((code) => code.trim())
        .filter((code) => /^\d{4}$/.test(code))
    )
  ).sort((a, b) => a.localeCompare(b));
}

function normalizeCodesArray(codes: unknown): string[] {
  if (!Array.isArray(codes)) return [];
  return Array.from(
    new Set(
      codes
        .map((code) => String(code || "").trim())
        .filter((code) => /^\d{4}$/.test(code))
    )
  ).sort((a, b) => a.localeCompare(b));
}

function coverageFromPostalCodes(postalCodes?: PostalCodesPayload): Coverage {
  const coverage = emptyCoverage();

  (["CABA", "GBA1", "GBA2", "GBA3"] as PostalTier[]).forEach((tier) => {
    const rangeKey = tierToRangeKey(tier);
    const codes = normalizeCodesArray(postalCodes?.[tier]);

    coverage[rangeKey] = {
      mode: "cp",
      postalCodes: codes.join(","),
      zone: "",
    };
  });

  return coverage;
}

function getAllCodesFromCoverage(coverage: Coverage): string[] {
  return Array.from(
    new Set(RANGE_KEYS.flatMap((key) => parsePostalCodes(coverage[key].postalCodes)))
  ).sort((a, b) => a.localeCompare(b));
}

function normalizeTariff(row: TariffApiRow): PriceListItem {
  const rawName = row?.name || "";
  const detectedType = apiTypeToUiType(row?.type);

  return {
    id: row?.id ?? null,
    code: row?.id ? `T${row.id}` : "",
    name: rawName,
    rawName,
    type: detectedType,
    priceCABA: row?.price_caba ?? "",
    priceGBA1: row?.price_gba1 ?? "",
    priceGBA2: row?.price_gba2 ?? "",
    priceGBA3: row?.price_gba3 ?? "",
    priceTurbo: row?.price_turbo ?? "",
    coverage: coverageFromPostalCodes(row?.postal_codes),
  };
}

function extractTariffRows(json: TariffsResponse): TariffApiRow[] {
  if (Array.isArray(json?.tariffs)) return json.tariffs;
  if (Array.isArray(json?.data)) return json.data;
  if (json?.tariff && typeof json.tariff === "object") return [json.tariff];
  if (json?.data && !Array.isArray(json.data) && typeof json.data === "object" && "name" in json.data) {
    return [json.data as TariffApiRow];
  }
  return [];
}

const pageWrapStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "20px",
};

const headerRowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "16px",
  flexWrap: "wrap",
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "34px",
  fontWeight: 800,
  color: "var(--text-primary)",
  letterSpacing: "-0.03em",
};

const subtitleStyle: React.CSSProperties = {
  margin: "8px 0 0 0",
  color: "var(--text-secondary)",
  fontSize: "15px",
  lineHeight: 1.6,
};

const actionsWrapStyle: React.CSSProperties = {
  display: "flex",
  gap: "12px",
  flexWrap: "wrap",
};

const kpiGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: "16px",
};

const kpiLabelStyle: React.CSSProperties = {
  fontSize: "13px",
  color: "var(--text-secondary)",
  marginBottom: "10px",
};

const kpiValueStyle: React.CSSProperties = {
  fontSize: "28px",
  fontWeight: 800,
  color: "var(--text-primary)",
  letterSpacing: "-0.03em",
};

const kpiMetaStyle: React.CSSProperties = {
  marginTop: "10px",
  fontSize: "13px",
  color: "var(--text-secondary)",
};

const sectionTitleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "18px",
  fontWeight: 700,
  color: "var(--text-primary)",
};

const sectionTextStyle: React.CSSProperties = {
  margin: "6px 0 0 0",
  color: "var(--text-secondary)",
  fontSize: "14px",
};

const tableWrapStyle: React.CSSProperties = {
  overflowX: "auto",
  border: "1px solid var(--border)",
  borderRadius: "18px",
  background: "rgba(255,255,255,0.02)",
};

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
};

const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "14px 12px",
  color: "var(--text-secondary)",
  fontSize: "13px",
  fontWeight: 700,
  borderBottom: "1px solid var(--border)",
  background: "rgba(255,255,255,0.02)",
};

const tdStyle: React.CSSProperties = {
  padding: "14px 12px",
  color: "var(--text-primary)",
  fontSize: "14px",
  borderBottom: "1px solid var(--border)",
  verticalAlign: "middle",
};

const modalOverlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 100,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(0,0,0,0.65)",
  padding: "16px",
};

const modalCardStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: "1280px",
  maxHeight: "85vh",
  overflow: "hidden",
  padding: 0,
};

const modalHeaderStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  borderBottom: "1px solid var(--border)",
  padding: "18px 20px",
};

const modalBodyStyle: React.CSSProperties = {
  maxHeight: "75vh",
  overflowY: "auto",
  padding: "20px",
};

const softBoxStyle: React.CSSProperties = {
  width: "100%",
  minHeight: "48px",
  display: "flex",
  alignItems: "center",
  borderRadius: "16px",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  padding: "0 16px",
  color: "var(--text-secondary)",
  fontSize: "14px",
};

const selectStyle: React.CSSProperties = {
  width: "100%",
  minHeight: "48px",
  borderRadius: "16px",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  color: "var(--text-primary)",
  padding: "0 14px",
  fontSize: "14px",
};

function Modal({ open, title, onClose, children }: ModalProps) {
  if (!open) return null;

  return (
    <div style={modalOverlayStyle}>
      <div style={{ position: "absolute", inset: 0 }} onClick={onClose} />
      <div style={{ position: "relative", width: "100%", maxWidth: "1280px" }}>
        <Card style={modalCardStyle}>
          <div style={modalHeaderStyle}>
            <div>
              <h2
                style={{
                  margin: 0,
                  fontSize: "26px",
                  fontWeight: 700,
                  color: "var(--text-primary)",
                }}
              >
                {title}
              </h2>
              <p
                style={{
                  margin: "6px 0 0 0",
                  fontSize: "14px",
                  color: "var(--text-secondary)",
                }}
              >
                Definí valores por tramo y cómo querés identificar cada rango.
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              style={{
                background: "transparent",
                border: "none",
                color: "var(--text-secondary)",
                fontSize: "22px",
                cursor: "pointer",
              }}
            >
              ×
            </button>
          </div>

          <div style={modalBodyStyle}>{children}</div>
        </Card>
      </div>
    </div>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        display: "block",
        marginBottom: "8px",
        fontSize: "12px",
        color: "var(--text-secondary)",
      }}
    >
      {children}
    </span>
  );
}

function Select({
  label,
  children,
  ...props
}: React.SelectHTMLAttributes<HTMLSelectElement> & { label: string }) {
  return (
    <label style={{ display: "block" }}>
      <FieldLabel>{label}</FieldLabel>
      <select {...props} style={selectStyle}>
        {children}
      </select>
    </label>
  );
}

function Segmented({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (value: CoverageMode) => void;
  options: Array<{ label: string; value: CoverageMode }>;
}) {
  return (
    <div
      style={{
        display: "inline-flex",
        borderRadius: "12px",
        border: "1px solid var(--border)",
        background: "var(--surface-alt)",
        padding: "4px",
      }}
    >
      {options.map((opt) => {
        const active = value === opt.value;

        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            style={{
              border: "none",
              cursor: "pointer",
              borderRadius: "10px",
              padding: "8px 14px",
              fontSize: "13px",
              fontWeight: active ? 700 : 500,
              background: active ? "var(--primary)" : "transparent",
              color: active ? "#000000" : "var(--text-secondary)",
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function CoverageCell({
  rangeKey,
  coverage,
  onPostalChange,
}: {
  rangeKey: RangeKey;
  coverage: Coverage;
  onPostalChange: (rangeKey: RangeKey, value: string) => void;
}) {
  const current = coverage[rangeKey];

  return (
    <div>
      <Input
        type="text"
        value={current.postalCodes}
        onChange={(e) => onPostalChange(rangeKey, e.target.value)}
        placeholder="Ej: 1706,1708,1712"
      />

      <p
        style={{
          margin: "6px 0 0 0",
          fontSize: "12px",
          color: "var(--text-secondary)",
        }}
      >
        Códigos postales de 4 dígitos separados por coma. Se guardan en backend
        y se devuelven en la tarifa externa del store.
      </p>

      {current.postalCodes && !isValidPostalCodes(current.postalCodes) ? (
        <p
          style={{
            margin: "6px 0 0 0",
            fontSize: "12px",
            color: "var(--warning)",
          }}
        >
          Formato inválido. Usá CP de 4 dígitos separados por coma.
        </p>
      ) : null}
    </div>
  );
}

export default function PriceLists() {
  const { token } = useAuth();

  const [allItems, setAllItems] = useState<PriceListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [activeType, setActiveType] = useState<PriceListType>("store");
  const [searchCode, setSearchCode] = useState("");
  const [searchName, setSearchName] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [originalCoverage, setOriginalCoverage] = useState<Coverage>(emptyCoverage());
  const [isEditing, setIsEditing] = useState(false);

  const loadTariffs = async () => {
    setLoading(true);
    setLoadError(null);

    try {
      const res = await authFetch("/v2/tariffs", { token });
      const json: TariffsResponse = await res.json().catch(() => ({}));

      if (!res.ok || json?.ok === false) {
        throw new Error(json?.error || "No se pudieron cargar las tarifas");
      }

      const rows = extractTariffRows(json);
      const normalized = rows.map(normalizeTariff);

      setAllItems(normalized);
      return normalized;
    } catch (error) {
      console.error(error);
      const message =
        error instanceof Error ? error.message : "Error cargando listas de precios";
      setLoadError(message);
      setAllItems([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTariffs();
  }, [token]);

  const visibleItems = useMemo(() => {
    return allItems
      .filter((item) => item.type === activeType)
      .filter((item) =>
        searchCode.trim()
          ? item.code.toLowerCase().includes(searchCode.trim().toLowerCase())
          : true
      )
      .filter((item) =>
        searchName.trim()
          ? item.name.toLowerCase().includes(searchName.trim().toLowerCase())
          : true
      );
  }, [allItems, activeType, searchCode, searchName]);

  const totals = useMemo(() => {
    const stores = allItems.filter((item) => item.type === "store").length;
    const drivers = allItems.filter((item) => item.type === "driver").length;

    return {
      total: allItems.length,
      stores,
      drivers,
      visible: visibleItems.length,
    };
  }, [allItems, visibleItems]);

  const loadTariffDetail = async (id: number): Promise<PriceListItem | null> => {
    const res = await authFetch(`/v2/tariffs/${id}`, { token });
    const json: TariffsResponse = await res.json().catch(() => ({}));

    if (!res.ok || json?.ok === false) {
      throw new Error(json?.error || "No se pudo cargar el detalle de la tarifa");
    }

    const rows = extractTariffRows(json);
    const tariff = rows[0];

    return tariff ? normalizeTariff(tariff) : null;
  };

  const syncTariffPostalCodes = async ({
    tariffId,
    previousCoverage,
    nextCoverage,
  }: {
    tariffId: number;
    previousCoverage: Coverage;
    nextCoverage: Coverage;
  }) => {
    const previousCodes = getAllCodesFromCoverage(previousCoverage);

    if (previousCodes.length > 0) {
      const deleteRes = await authFetch(`/v2/tariffs/${tariffId}/postal-codes`, {
        method: "DELETE",
        token,
        json: true,
        body: JSON.stringify({ codes: previousCodes }),
      });

      const deleteJson = await deleteRes.json().catch(() => ({}));

      if (!deleteRes.ok || deleteJson?.ok === false) {
        throw new Error(deleteJson?.error || "No se pudieron limpiar los CP anteriores");
      }
    }

    for (const rangeKey of RANGE_KEYS) {
      const tier = rangeKeyToTier(rangeKey);
      const codes = parsePostalCodes(nextCoverage[rangeKey].postalCodes);

      if (codes.length === 0) continue;

      const postRes = await authFetch(`/v2/tariffs/${tariffId}/postal-codes`, {
        method: "POST",
        token,
        json: true,
        body: JSON.stringify({ tier, codes }),
      });

      const postJson = await postRes.json().catch(() => ({}));

      if (!postRes.ok || postJson?.ok === false) {
        throw new Error(
          postJson?.error || `No se pudieron guardar los CP de ${tier}`
        );
      }
    }
  };

  const openCreateModal = (type: PriceListType) => {
    const coverage = emptyCoverage();
    setForm({ ...emptyForm, type, coverage });
    setOriginalCoverage(emptyCoverage());
    setIsEditing(false);
    setModalOpen(true);
  };

  const openEditModal = async (item: PriceListItem) => {
    let itemToEdit = item;

    try {
      if (item.id) {
        const detailed = await loadTariffDetail(item.id);
        if (detailed) itemToEdit = detailed;
      }
    } catch (error) {
      console.error(error);
      alert(
        error instanceof Error
          ? error.message
          : "No se pudo cargar el detalle de la tarifa"
      );
    }

    setForm({
      id: itemToEdit.id,
      code: itemToEdit.code,
      name: itemToEdit.name,
      type: itemToEdit.type,
      priceCABA: itemToEdit.priceCABA,
      priceGBA1: itemToEdit.priceGBA1,
      priceGBA2: itemToEdit.priceGBA2,
      priceGBA3: itemToEdit.priceGBA3,
      priceTurbo: itemToEdit.priceTurbo,
      coverage: itemToEdit.coverage || emptyCoverage(),
    });
    setOriginalCoverage(itemToEdit.coverage || emptyCoverage());
    setIsEditing(true);
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setForm({ ...emptyForm, coverage: emptyCoverage() });
    setOriginalCoverage(emptyCoverage());
    setIsEditing(false);
  };

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const setCoveragePostalCodes = (rangeKey: RangeKey, rawValue: string) => {
    const sanitized = sanitizePostalCodes(rawValue);

    setForm((prev) => ({
      ...prev,
      coverage: {
        ...prev.coverage,
        [rangeKey]: {
          ...prev.coverage[rangeKey],
          postalCodes: sanitized,
        },
      },
    }));
  };

  const validateCoverage = () => {
    const labels: Record<RangeKey, string> = {
      caba: "CABA",
      gba1: "GBA 1",
      gba2: "GBA 2",
      gba3: "GBA 3",
    };

    for (const key of RANGE_KEYS) {
      const current = form.coverage[key];

      if (!isValidPostalCodes(current.postalCodes)) {
        return `Revisá los CP de ${labels[key]}. Deben ser códigos de 4 dígitos separados por coma.`;
      }
    }

    return null;
  };

  const validate = () => {
    if (!form.name.trim()) {
      alert("Completá el nombre de la lista.");
      return false;
    }

    const prices = [form.priceCABA, form.priceGBA1, form.priceGBA2, form.priceGBA3];

    for (const p of prices) {
      if (String(p).trim() === "") {
        alert("Completá los 4 valores.");
        return false;
      }
      if (Number.isNaN(Number(p))) {
        alert("Los valores deben ser numéricos.");
        return false;
      }
    }

    // Turbo es opcional (NULL = TURBO_PRICE_NOT_CONFIGURED) — pero si se
    // completa, tiene que ser un número válido, igual que los demás.
    if (String(form.priceTurbo).trim() !== "" && Number.isNaN(Number(form.priceTurbo))) {
      alert("El precio Turbo debe ser numérico (o dejarse vacío si no aplica).");
      return false;
    }

    const coverageError = validateCoverage();
    if (coverageError) {
      alert(coverageError);
      return false;
    }

    return true;
  };

  const extractCreatedId = (json: TariffsResponse) => {
    return json?.tariffID || json?.id || (json?.data && !Array.isArray(json.data) ? json.data.id : null) || null;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);

    try {
      let tariffId = form.id;

      if (isEditing && form.id) {
        const payload = {
          tariff: {
            name: form.name.trim(),
            type: uiTypeToApiType(form.type),
            price_caba: Number(form.priceCABA),
            price_gba1: Number(form.priceGBA1),
            price_gba2: Number(form.priceGBA2),
            price_gba3: Number(form.priceGBA3),
            price_turbo: String(form.priceTurbo).trim() === "" ? null : Number(form.priceTurbo),
          },
        };

        const res = await authFetch(`/v2/tariffs/${form.id}`, {
          method: "PUT",
          token,
          json: true,
          body: JSON.stringify(payload),
        });

        const json: TariffsResponse = await res.json().catch(() => ({}));

        if (!res.ok || json?.ok === false) {
          throw new Error(json?.error || "No se pudo actualizar la lista");
        }
      } else {
        const payload = {
          name: form.name.trim(),
          type: uiTypeToApiType(form.type),
          price_caba: Number(form.priceCABA),
          price_gba1: Number(form.priceGBA1),
          price_gba2: Number(form.priceGBA2),
          price_gba3: Number(form.priceGBA3),
          price_turbo: String(form.priceTurbo).trim() === "" ? null : Number(form.priceTurbo),
        };

        const res = await authFetch("/v2/tariffs", {
          method: "POST",
          token,
          json: true,
          body: JSON.stringify(payload),
        });

        const json: TariffsResponse = await res.json().catch(() => ({}));

        if (!res.ok || json?.ok === false) {
          throw new Error(json?.error || json?.message || "No se pudo crear la lista");
        }

        tariffId = extractCreatedId(json);
      }

      if (!tariffId) {
        throw new Error("No se pudo resolver el ID de la tarifa para guardar los CP.");
      }

      await syncTariffPostalCodes({
        tariffId: Number(tariffId),
        previousCoverage: isEditing ? originalCoverage : emptyCoverage(),
        nextCoverage: form.coverage,
      });

      await loadTariffs();
      closeModal();
    } catch (error) {
      console.error(error);
      alert((error as Error)?.message || "Error guardando la lista");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item: PriceListItem) => {
    const ok = window.confirm(
      `¿Eliminar la lista "${item.name}" de ${item.type === "store" ? "stores" : "drivers"}?`
    );
    if (!ok || !item.id) return;

    try {
      const res = await authFetch(`/v2/tariffs/${item.id}`, {
        method: "DELETE",
        token,
      });

      const json: TariffsResponse = await res.json().catch(() => ({}));

      if (!res.ok || json?.ok === false) {
        throw new Error(json?.error || "No se pudo eliminar la lista");
      }

      await loadTariffs();
    } catch (error) {
      console.error(error);
      alert(error instanceof Error ? error.message : "Error eliminando la lista");
    }
  };

  const clearFilters = () => {
    setSearchCode("");
    setSearchName("");
  };

  return (
    <div style={pageWrapStyle}>
      <div style={headerRowStyle}>
        <div>
          <h1 style={titleStyle}>Listas de precios</h1>
          <p style={subtitleStyle}>
            Tarifas separadas para stores y drivers, con cobertura por CP o zona.
          </p>
        </div>

        <div style={actionsWrapStyle}>
          <Button variant="secondary" onClick={loadTariffs}>
            Recargar
          </Button>
          <Button onClick={() => openCreateModal(activeType)}>Nueva lista</Button>
        </div>
      </div>

      <div style={kpiGridStyle} className="price-lists-kpi-grid">
        <Card>
          <div style={kpiLabelStyle}>Total listas</div>
          <div style={kpiValueStyle}>{totals.total}</div>
          <div style={kpiMetaStyle}>Cantidad total registrada.</div>
        </Card>

        <Card>
          <div style={kpiLabelStyle}>Listas stores</div>
          <div style={kpiValueStyle}>{totals.stores}</div>
          <div style={kpiMetaStyle}>Tarifas disponibles para stores.</div>
        </Card>

        <Card>
          <div style={kpiLabelStyle}>Listas drivers</div>
          <div style={kpiValueStyle}>{totals.drivers}</div>
          <div style={kpiMetaStyle}>Tarifas disponibles para drivers.</div>
        </Card>

        <Card>
          <div style={kpiLabelStyle}>Visibles</div>
          <div style={kpiValueStyle}>{totals.visible}</div>
          <div style={kpiMetaStyle}>Resultados según tipo y filtros.</div>
        </Card>
      </div>

      <Card>
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "18px" }}>
          <button
            type="button"
            onClick={() => setActiveType("store")}
            style={{
              border: "none",
              cursor: "pointer",
              borderRadius: "999px",
              padding: "10px 16px",
              fontSize: "14px",
              fontWeight: 700,
              background: activeType === "store" ? "var(--primary)" : "var(--surface-alt)",
              color: activeType === "store" ? "#000000" : "var(--text-primary)",
            }}
          >
            Stores
          </button>

          <button
            type="button"
            onClick={() => setActiveType("driver")}
            style={{
              border: "none",
              cursor: "pointer",
              borderRadius: "999px",
              padding: "10px 16px",
              fontSize: "14px",
              fontWeight: 700,
              background: activeType === "driver" ? "var(--primary)" : "var(--surface-alt)",
              color: activeType === "driver" ? "#000000" : "var(--text-primary)",
            }}
          >
            Drivers
          </button>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
            gap: "16px",
            marginBottom: "20px",
          }}
          className="price-lists-filters-grid"
        >
          <div>
            <FieldLabel>Código</FieldLabel>
            <Input value={searchCode} onChange={(e) => setSearchCode(e.target.value)} />
          </div>

          <div>
            <FieldLabel>Nombre</FieldLabel>
            <Input value={searchName} onChange={(e) => setSearchName(e.target.value)} />
          </div>

          <div style={{ display: "flex", alignItems: "flex-end" }}>
            <Button variant="secondary" fullWidth>
              Filtrar
            </Button>
          </div>

          <div style={{ display: "flex", alignItems: "flex-end" }}>
            <Button variant="secondary" fullWidth onClick={clearFilters}>
              Limpiar filtros
            </Button>
          </div>
        </div>

        <div style={{ marginBottom: "14px" }}>
          <h2 style={sectionTitleStyle}>Tarifas registradas</h2>
          <p style={sectionTextStyle}>
            Listado general por tipo con edición y eliminación.
          </p>
        </div>

        {loadError ? (
          <div
            style={{
              marginBottom: "14px",
              color: "var(--danger)",
              fontSize: "14px",
            }}
          >
            {loadError}
          </div>
        ) : null}

        <div style={tableWrapStyle}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Código</th>
                <th style={thStyle}>Nombre</th>
                <th style={{ ...thStyle, textAlign: "center" }}>CABA</th>
                <th style={{ ...thStyle, textAlign: "center" }}>GBA 1</th>
                <th style={{ ...thStyle, textAlign: "center" }}>GBA 2</th>
                <th style={{ ...thStyle, textAlign: "center" }}>GBA 3</th>
                <th style={{ ...thStyle, textAlign: "center" }}>Turbo</th>
                <th style={{ ...thStyle, textAlign: "center" }}>Tipo</th>
                <th style={{ ...thStyle, textAlign: "center" }}>Acciones</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={9}
                    style={{ ...tdStyle, textAlign: "center", color: "var(--text-secondary)" }}
                  >
                    Cargando listas...
                  </td>
                </tr>
              ) : visibleItems.length === 0 ? (
                <tr>
                  <td
                    colSpan={9}
                    style={{ ...tdStyle, textAlign: "center", color: "var(--text-secondary)" }}
                  >
                    No hay listas para este tipo.
                  </td>
                </tr>
              ) : (
                visibleItems.map((item) => (
                  <tr key={item.id ?? item.rawName}>
                    <td style={{ ...tdStyle, color: "var(--text-secondary)" }}>{item.code}</td>
                    <td style={tdStyle}>{item.name}</td>
                    <td style={{ ...tdStyle, textAlign: "center", color: "var(--text-secondary)" }}>
                      {formatMoney(item.priceCABA)}
                    </td>
                    <td style={{ ...tdStyle, textAlign: "center", color: "var(--text-secondary)" }}>
                      {formatMoney(item.priceGBA1)}
                    </td>
                    <td style={{ ...tdStyle, textAlign: "center", color: "var(--text-secondary)" }}>
                      {formatMoney(item.priceGBA2)}
                    </td>
                    <td style={{ ...tdStyle, textAlign: "center", color: "var(--text-secondary)" }}>
                      {formatMoney(item.priceGBA3)}
                    </td>
                    <td style={{ ...tdStyle, textAlign: "center", color: "var(--text-secondary)" }}>
                      {String(item.priceTurbo).trim() === "" ? "—" : formatMoney(item.priceTurbo)}
                    </td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minHeight: "30px",
                          padding: "0 12px",
                          borderRadius: "999px",
                          fontSize: "12px",
                          fontWeight: 700,
                          background:
                            item.type === "store"
                              ? "var(--primary-soft)"
                              : "rgba(255,255,255,0.06)",
                          color: "var(--text-primary)",
                          border: "1px solid var(--border)",
                        }}
                      >
                        {item.type === "store" ? "Store" : "Driver"}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          gap: "8px",
                          flexWrap: "wrap",
                        }}
                      >
                        <Button
                          variant="secondary"
                          onClick={() => openEditModal(item)}
                          style={{ minHeight: "36px", padding: "8px 12px", fontSize: "12px" }}
                        >
                          Editar
                        </Button>
                        <Button
                          variant="danger"
                          onClick={() => handleDelete(item)}
                          style={{ minHeight: "36px", padding: "8px 12px", fontSize: "12px" }}
                        >
                          Eliminar
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={isEditing ? "Editar lista de precios" : "Nueva lista de precios"}
      >
        <form onSubmit={handleSubmit}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
              gap: "16px",
              marginBottom: "20px",
            }}
            className="price-lists-form-grid"
          >
            <div>
              <FieldLabel>Código</FieldLabel>
              <div style={softBoxStyle}>{form.code || "Se genera automáticamente"}</div>
            </div>

            <div>
              <FieldLabel>Nombre</FieldLabel>
              <Input
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                placeholder={
                  form.type === "store"
                    ? "Ej: Tarifa store estándar"
                    : "Ej: Tarifa driver estándar"
                }
              />
            </div>

            <Select
              label="Tipo"
              value={form.type}
              onChange={(e) => setField("type", e.target.value as PriceListType)}
            >
              <option value="store">Store</option>
              <option value="driver">Driver</option>
            </Select>

            <div>
              <FieldLabel>Estado</FieldLabel>
              <div style={softBoxStyle}>Los CP se guardan en backend y salen por la API externa.</div>
            </div>
          </div>

          <div style={tableWrapStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Tramo</th>
                  <th style={thStyle}>CP / Zona</th>
                  <th style={thStyle}>Valor</th>
                </tr>
              </thead>

              <tbody>
                {[
                  { key: "caba" as RangeKey, label: "CABA", field: "priceCABA" as const },
                  { key: "gba1" as RangeKey, label: "GBA 1", field: "priceGBA1" as const },
                  { key: "gba2" as RangeKey, label: "GBA 2", field: "priceGBA2" as const },
                  { key: "gba3" as RangeKey, label: "GBA 3", field: "priceGBA3" as const },
                ].map((row) => (
                  <tr key={row.key}>
                    <td style={{ ...tdStyle, fontWeight: 700 }}>{row.label}</td>
                    <td style={tdStyle}>
                      <CoverageCell
                        rangeKey={row.key}
                        coverage={form.coverage}
                        onPostalChange={setCoveragePostalCodes}
                      />
                    </td>
                    <td style={tdStyle}>
                      <Input
                        type="number"
                        step="0.01"
                        value={String(form[row.field])}
                        onChange={(e) => setField(row.field, e.target.value)}
                        placeholder="0"
                      />
                    </td>
                  </tr>
                ))}
                {/* Turbo: modalidad de servicio, no zona postal — nunca lleva
                    CP/tier (ver CoverageCell arriba). Vacío = sin configurar,
                    no se fuerza a cargar. */}
                <tr>
                  <td style={{ ...tdStyle, fontWeight: 700 }}>Turbo</td>
                  <td style={{ ...tdStyle, color: "var(--text-secondary)", fontSize: "13px" }}>
                    No es una zona postal
                  </td>
                  <td style={tdStyle}>
                    <Input
                      type="number"
                      step="0.01"
                      value={String(form.priceTurbo)}
                      onChange={(e) => setField("priceTurbo", e.target.value)}
                      placeholder="Sin configurar"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div
            style={{
              marginTop: "24px",
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <Button type="button" variant="secondary" onClick={closeModal} disabled={saving}>
              Volver
            </Button>

            <Button type="submit" disabled={saving}>
              {saving ? "Guardando..." : isEditing ? "Guardar cambios" : "Guardar"}
            </Button>
          </div>
        </form>
      </Modal>

      <style>
        {`
          .price-lists-kpi-grid {
            grid-template-columns: repeat(4, minmax(0, 1fr));
          }

          .price-lists-filters-grid {
            grid-template-columns: repeat(4, minmax(0, 1fr));
          }

          .price-lists-form-grid {
            grid-template-columns: repeat(4, minmax(0, 1fr));
          }

          @media (max-width: 1180px) {
            .price-lists-kpi-grid {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            }

            .price-lists-filters-grid {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            }

            .price-lists-form-grid {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            }
          }

          @media (max-width: 720px) {
            .price-lists-kpi-grid,
            .price-lists-filters-grid,
            .price-lists-form-grid {
              grid-template-columns: 1fr !important;
            }
          }
        `}
      </style>
    </div>
  );
}